import { h, render, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { IntlProvider, Text, Localizer, withText } from 'preact-i18n';
import { route } from 'preact-router';
import Markdown from 'preact-markdown';
import linkstate from 'linkstate';

import localUtil from '../../lib/util';
import fields from '../../lib/fields';
import { printViaIframe } from '../../lib/printHelper';

import ViewFields from '../../components/form/viewFields';
import Input from '../../components/form/input';
import DyrejournalAnimalListMini from '../../components/dyrejournal/listAnimalMini';
import DyrejournalPrescriptionListMini from '../../components/dyrejournal/listPrescriptionMini';
import DyrejournalJournalPrint from '../../components/dyrejournal/journalPrint';

import ImageScroller from '../../components/imagescroller';
import DragNDropFileupload from '../../components/form/dragNdropFileupload';

import definitionNo from '../../languages/no.json';
import definitionEn from '../../languages/en.json';
import { animalAge } from '../../lib/animalAge';

const countryMap = {
    no: definitionNo,
    en: definitionEn,
    default: definitionEn,
};

@observer
class SendJournal extends Component {
	constructor(props) {
        super(props);
        this.state = {
            toEmail: props.toEmail || '',
            subject: '',
            body: '',
            includeImages: false,
            includeFiles: false,
            includeReceipt: false,
            includeBodyText: true,
            showJournalPicker: false,
            additionalJournalIds: [],
            selectedJournalFiles: null,  // null means "all selected" (default), Set means explicit selection
            selectedAnimalFiles: new Set(),
            uploadedFiles: [],
            saveToAnimal: {},
            idexxResults: [],           // IDEXX lab results for the animals in this drawer (TICKET-01579)
            selectedIdexxResults: null, // null means "all selected" (default), Set of result ids means explicit selection
        };
        this.searchTimer = null;
    }

    onClickPrintJournal = e => {
        // console.log('onClickPrintSale', e);
        const { journals = [] } = this.props;
        const { userStore } = this.props.stores;
        const { user } = userStore;
        const { language = 'no' } = user;
        const languageDef = countryMap[language || 'default'];

        // Render Receipt component with data
        const receipt = <IntlProvider definition={languageDef}>
            <DyrejournalJournalPrint stores={this.props.stores} journals={journals} />
        </IntlProvider>;

        // Append to document temporarily to render
        const tempDiv = document.createElement('div');
        tempDiv.className = 'receipt';
        document.body.appendChild(tempDiv);
        render(receipt, tempDiv);
        const htmlContent = tempDiv.outerHTML;
        document.body.removeChild(tempDiv);
        render(null, tempDiv);
        printViaIframe(htmlContent);
    }

    formatBytes = (bytes) => {
        if (!bytes || bytes <= 0) return '0 B';
        if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
        if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
        return `${bytes} B`;
    }

    // TICKET-01437: journal files imported from a clinic's previous system have an empty
    // `name`, so the attachment list rendered blank rows and the vet could not see what was
    // about to be emailed. Fall back to the stored filename / the S3 basename.
    fileLabel = (file) => {
        if (!file) return '';
        if (file.name) return file.name;
        if (file.newFilename) return file.newFilename;
        const last = `${file.s3Link || ''}`.split('/').pop();
        if (!last) return '';
        try {
            return decodeURIComponent(last);
        } catch (e) {
            return last;
        }
    }

    // TICKET-01437: attachments that came in with a clinic's legacy import are linked to the
    // wrong journal for most of the imported clinics (a client was emailed four other
    // clients' invoices). They stay listed and can still be ticked deliberately, but they are
    // kept OUT of the default selection so nothing rides along unnoticed.
    isImportedFile = (file) => {
        const source = `${file?.newFilename || file?.s3Link || ''}`;
        return source.indexOf('-imported-') > -1;
    }

    // `selectedJournalFiles === null` means the user has not touched the selection yet: every
    // journal file is on except the imported ones. Once a checkbox is toggled the Set is the
    // whole truth.
    isJournalFileSelected = (file, selectedJournalFiles) => {
        if (selectedJournalFiles === null) return !this.isImportedFile(file);
        return selectedJournalFiles.has(file.s3Link);
    }

    // TICKET-01579: IDEXX lab results live in their own collection and were never part of a
    // forwarded journal. Only complete results with a PDF, linked to a journal that is being
    // sent, are offered as attachments.
    getIdexxResultsForSend = () => {
        const { idexxResults = [], additionalJournalIds = [] } = this.state;
        const { journals = [] } = this.props;
        const journalIds = new Set([...journals.map(j => j.id), ...additionalJournalIds]);
        return idexxResults.filter(result => result
            && journalIds.has(result.journal)
            && result.status === 'COMPLETE'
            && result.hasPdf !== false);
    }

    isIdexxResultSelected = (result, selectedIdexxResults) => {
        if (selectedIdexxResults === null) return true;
        return selectedIdexxResults.has(result.id);
    }

    getSelectedIdexxResultIds = () => {
        const { selectedIdexxResults } = this.state;
        return this.getIdexxResultsForSend()
            .filter(result => this.isIdexxResultSelected(result, selectedIdexxResults))
            .map(result => result.id);
    }

    getSelectedFiles = () => {
        const { selectedJournalFiles, selectedAnimalFiles, uploadedFiles = [], additionalJournalIds = [] } = this.state;
        const { journalStore } = this.props.stores;
        const { journalsToSend, allAnimalJournals, animals } = journalStore;
        const effectiveJournals = [
            ...(journalsToSend || []),
            ...(allAnimalJournals || []).filter(j => additionalJournalIds.includes(j.id)),
        ];

        const selected = [];
        effectiveJournals.forEach(journal => {
            (journal.files || []).forEach(file => {
                if (this.isJournalFileSelected(file, selectedJournalFiles)) selected.push(file);
            });
        });
        (animals || []).forEach(animal => {
            (animal.files || []).forEach(file => {
                if (selectedAnimalFiles.has(file.s3Link)) selected.push(file);
            });
            (animal.images || []).forEach(image => {
                if (selectedAnimalFiles.has(image.s3Link)) selected.push(image);
            });
        });
        uploadedFiles.forEach(file => selected.push(file));
        return selected;
    }

    getTotalAttachmentBytes = () => {
        return this.getSelectedFiles().reduce((sum, f) => sum + (f.bytes || 0), 0);
    }

    onClickEmailJournal = async e => {
        const { saleIds = [], additionalJournalIds = [], selectedJournalFiles, selectedAnimalFiles, uploadedFiles = [], saveToAnimal = {} } = this.state;
        this.setState({
            journalSent: false,
            journalSending: true,
            sendResponse: null,
            sendError: null,
        });
        const { toEmail, includeReceipt, subject, body, includeImages, includeBodyText } = this.state;
        const { journals = [] } = this.props;
        const { journalStore, animalStore } = this.props.stores;

        // Combine original + additional journal IDs
        const originalJournalIds = journals.map(journal => journal.id);
        const allJournalIds = [...originalJournalIds, ...additionalJournalIds];

        // Build additional attachment URLs (animal files + uploaded files)
        const additionalUrls = [];
        selectedAnimalFiles.forEach(url => additionalUrls.push(url));
        uploadedFiles.forEach(file => additionalUrls.push(file.s3Link));

        // Build excluded file URLs (journal files the user unchecked, plus the legacy imported
        // files that are left unchecked by default — TICKET-01437). This runs even when the
        // user never touched the selection, so the default is enforced on the wire too.
        // The backend treats this list as a blacklist, so a journal we fail to look at here is a
        // journal whose files all go out. `journals` (the props copy the drawer was opened with)
        // is folded in as well, so an empty/stale journalsToSend cannot silently re-enable them.
        const { allAnimalJournals, journalsToSend } = journalStore;
        const effectiveJournals = [
            ...(journalsToSend || []),
            ...journals,
            ...(allAnimalJournals || []).filter(j => additionalJournalIds.includes(j.id)),
        ];
        // Imported files are opt-in on the backend as well (TICKET-01701): only the ones the
        // user ticked deliberately are named in includeFileUrls, everything else stays home.
        const excluded = new Set();
        const included = new Set();
        effectiveJournals.forEach(journal => {
            if (journal.files && journal.files.length > 0) {
                journal.files.forEach(file => {
                    if (!this.isJournalFileSelected(file, selectedJournalFiles)) {
                        excluded.add(file.s3Link);
                    } else if (this.isImportedFile(file)) {
                        included.add(file.s3Link);
                    }
                });
            }
        });
        const excludedUrls = [...excluded];
        const includedUrls = [...included];

        // Save uploaded files to animal records if requested
        for (const file of uploadedFiles) {
            if (saveToAnimal[file.s3Link]) {
                const { animals } = journalStore;
                if (animals.length > 0) {
                    const animal = animals[0];
                    await animalStore.saveField(animal.id, 'files', [{
                        ...file,
                        date: new Date(),
                    }], true);
                }
            }
        }

        const attachmentUrls = additionalUrls.join(',');
        const excludeFileUrls = excludedUrls.join(',');
        const includeFileUrls = includedUrls.join(',');
        const idexxResultIds = this.getSelectedIdexxResultIds().join(',');

        const response = await journalStore.sendJournalEmail(
            allJournalIds, toEmail, includeReceipt, subject, body,
            includeImages, true, saleIds?.join(','), includeBodyText,
            attachmentUrls, excludeFileUrls, idexxResultIds, includeFileUrls,
        );
        // The backend reports per-recipient delivery in data.failed / data.allSent. A missing
        // response means the request itself failed. Only show the green "sent" state when the
        // email actually went out, otherwise surface the reason so it is not a silent failure.
        const data = response?.data || null;
        const failed = (data && Array.isArray(data.failed)) ? data.failed : [];
        const ok = !!response && data?.allSent !== false && failed.length === 0;
        this.setState({
            journalSent: ok,
            journalSending: false,
            sendResponse: data,
            sendError: ok ? null : (failed.length > 0 ? failed : [{ to: toEmail || '', error: 'send-failed' }]),
        });
    }

    sendErrorText = (code) => {
        switch (code) {
            case 'attachment-too-large':
                return <Text id='dyrejournal.email-error-attachment-too-large'>The attachments are too large to send by email. Remove some files or images and try again.</Text>;
            case 'missing-recipient':
                return <Text id='dyrejournal.email-error-missing-recipient'>The recipient email address is missing.</Text>;
            case 'invalid-recipient':
                return <Text id='dyrejournal.email-error-invalid-recipient'>The recipient email address is invalid.</Text>;
            case 'sender-not-verified':
                return <Text id='dyrejournal.email-error-sender-not-verified'>The sender address is not verified.</Text>;
            default:
                return <Text id='dyrejournal.email-error-generic'>The email could not be sent. Please try again.</Text>;
        }
    }

    loadAll = async () => {
        const { animalStore, visitorStore, journalStore, saleStore } = this.props.stores;
        const { journals } = this.props;

        // Load journals
        const journalIds = journals.map(journal => journal.id);
        const journalsToSend = await journalStore.load({
            query: {
                id: journalIds,
            },
            skipUpdate: true,
        });
        await journalStore.loadVeterinaries();
        journalStore.updateKeyValue('journalsToSend', journalsToSend);

        // Load animals
        const animalIds = journals.map(journal => journal.animal);
        let animals = [];
        if (animalIds.length > 0) {
            animals = await animalStore.load({
                query: {
                    id: animalIds,
                },
                skipUpdate: true,
            });
            journalStore.updateKeyValue('animals', animals);
        }

        // Load IDEXX lab results for these animals so they can be attached (TICKET-01579)
        const { userStore } = this.props.stores;
        if (userStore.customer?.featureFlags?.idexx === 1 && animals.length > 0) {
            const idexxResults = [];
            for (const animal of animals) {
                const response = await util.fetchApi(`/api/integrations/idexx/results/search?animal=${animal.id}`);
                const results = response?.data?.results || response?.data || [];
                if (Array.isArray(results)) idexxResults.push(...results);
            }
            this.setState({ idexxResults });
        }

        // Load all journals for these animals (for the journal picker)
        if (animalIds.length > 0) {
            const allAnimalJournalsList = await journalStore.load({
                query: {
                    animal: animalIds,
                },
                skipUpdate: true,
                limit: 100,
                sort: '-date',
            });
            journalStore.updateKeyValue('allAnimalJournals', allAnimalJournalsList || []);
        }

        // Load owners
        const ownerIds = animals.map(animal => animal.owner);
        if (ownerIds.length > 0) {
            const owners = await visitorStore.load({
                query: {
                    id: ownerIds,
                },
                skipUpdate: true,
            });
            journalStore.updateKeyValue('owners', owners);
        }

        // Load sale
        const sales = await saleStore.load({
            query: {
                journal: journalIds,
            },
            skipUpdate: true,
        });
        journalStore.updateKeyValue('sales', sales);

        // Load deleted sales
        const deletedSales = await saleStore.load({
            query: {
                offline: 1,
                journal: journalIds,
            },
            skipUpdate: true,
        });
        journalStore.updateKeyValue('deletedSales', deletedSales);

        // Load other sales for this visitor
        const visitorSales = await saleStore.load({
            query: {
                visitor: ownerIds,
            },
            skipUpdate: true,
            limit: 25,
        });
        journalStore.updateKeyValue('visitorSales', visitorSales);
    }

    searchInput = (e) => {
        const search = e.target.value;
        this.setState({ search, toEmail: search });

        clearTimeout(this.searchTimer);
        if (search.length >= 3) {
            this.searchTimer = setTimeout(async () => {
                this.doSearch(search);
            }, 300);
        }
    }

    async doSearch(search) {
        // return;
        const { contactStore } = this.props.stores;
        if (search) {
            // console.log('doUpdateField.saveField', id, field, value);
            const result = await contactStore.load({ query: { search }, limit: 500, skipUpdate: false });
        }
    }

    clearSearch = () => {
        const { contactStore } = this.props.stores;
        this.setState({ search: '', toEmail: '' });
        contactStore.updateKeyValue('contacts', []);
        this.doSearch('');
    }

    handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            this.doSearch(this.state.search);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            this.clearSearch();
        }
    }

    onClickContact = e => {
        const { contactStore } = this.props.stores;
        const { email } = e.target.closest('tr').dataset;
        this.setState({ search: email, toEmail: email }, () => {
            contactStore.updateKeyValue('contacts', []);
        });
    }

    onClickEditContact = e => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('tr').dataset;
        const { drawerLevel = 1 } = this.props;
        const { appState, contactStore } = this.props.stores;
        const { drawerHeightMedium } = appState;
        appState.openDrawer('editContact', {
            height: drawerHeightMedium,
            id,
            callback: () => {
                appState.toggleDrawer(false, drawerLevel + 1);
            },
        }, drawerLevel + 1);
    }

    addSaleId = e => {
        e.preventDefault();
        e.stopPropagation();
        const { saleIds = [] } = this.state;
        const id = e.target.value;
        // console.log('addSaleId', e, id);
        // console.log('saleIds', saleIds, saleIds.includes(id));
        if (saleIds.includes(id)) {
            this.setState({ saleIds: saleIds.filter(e => e !== id) });
        } else {
            this.setState({ saleIds: [...saleIds, id] });
        }
    }

    toggleJournalPicker = () => {
        this.setState(prev => ({ showJournalPicker: !prev.showJournalPicker }));
    }

    toggleAdditionalJournal = e => {
        const id = parseInt(e.target.value, 10);
        const { additionalJournalIds = [] } = this.state;
        if (additionalJournalIds.includes(id)) {
            this.setState({ additionalJournalIds: additionalJournalIds.filter(jid => jid !== id) });
        } else {
            this.setState({ additionalJournalIds: [...additionalJournalIds, id] });
        }
    }

    toggleJournalFile = e => {
        const url = e.target.value;
        this.setState(prev => {
            let selected = prev.selectedJournalFiles;
            if (selected === null) {
                // First toggle: initialize from all current journal files
                const { journalStore } = this.props.stores;
                const { journalsToSend, allAnimalJournals } = journalStore;
                const { additionalJournalIds = [] } = prev;
                const effectiveJournals = [
                    ...journalsToSend,
                    ...(allAnimalJournals || []).filter(j => additionalJournalIds.includes(j.id)),
                ];
                selected = new Set();
                effectiveJournals.forEach(journal => {
                    if (journal.files && journal.files.length > 0) {
                        journal.files.forEach(file => {
                            // Seed from the same default the checkboxes render: imported
                            // files start off (TICKET-01437).
                            if (!this.isImportedFile(file)) selected.add(file.s3Link);
                        });
                    }
                });
            } else {
                selected = new Set(selected);
            }
            if (selected.has(url)) {
                selected.delete(url);
            } else {
                selected.add(url);
            }
            return { selectedJournalFiles: selected };
        });
    }

    toggleAnimalFile = e => {
        const url = e.target.value;
        this.setState(prev => {
            const selected = new Set(prev.selectedAnimalFiles);
            if (selected.has(url)) {
                selected.delete(url);
            } else {
                selected.add(url);
            }
            return { selectedAnimalFiles: selected };
        });
    }

    handleUploadedFile = (file) => {
        this.setState(prev => ({
            uploadedFiles: [...prev.uploadedFiles, file],
        }));
    }

    removeUploadedFile = e => {
        const url = e.target.closest('button').dataset.url;
        this.setState(prev => ({
            uploadedFiles: prev.uploadedFiles.filter(f => f.s3Link !== url),
            saveToAnimal: { ...prev.saveToAnimal, [url]: undefined },
        }));
    }

    toggleSaveToAnimal = e => {
        const url = e.target.value;
        this.setState(prev => ({
            saveToAnimal: { ...prev.saveToAnimal, [url]: !prev.saveToAnimal[url] },
        }));
    }

    toggleIdexxResult = e => {
        const id = parseInt(e.target.value, 10);
        this.setState(prev => {
            let selected = prev.selectedIdexxResults;
            if (selected === null) {
                // First toggle: seed from the default (every offered result is on)
                selected = new Set(this.getIdexxResultsForSend().map(result => result.id));
            } else {
                selected = new Set(selected);
            }
            if (selected.has(id)) {
                selected.delete(id);
            } else {
                selected.add(id);
            }
            return { selectedIdexxResults: selected };
        });
    }

    clearAllAttachments = () => {
        this.setState({
            selectedJournalFiles: new Set(),
            selectedAnimalFiles: new Set(),
            selectedIdexxResults: new Set(),
            uploadedFiles: [],
            saveToAnimal: {},
        });
    }

    renderJournalPicker() {
        const { showJournalPicker, additionalJournalIds = [] } = this.state;
        const { journals = [] } = this.props;
        const { journalStore, userStore } = this.props.stores;
        const { user } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');
        const { allAnimalJournals, animals, veterinaries } = journalStore;

        const originalJournalIds = journals.map(j => j.id);

        // Filter out journals that are already in the original selection
        const availableJournals = (allAnimalJournals || []).filter(j => !originalJournalIds.includes(j.id));

        if (availableJournals.length === 0) return null;

        // Group by animal
        const journalsByAnimal = {};
        availableJournals.forEach(journal => {
            const animalId = journal.animal;
            if (!journalsByAnimal[animalId]) {
                journalsByAnimal[animalId] = [];
            }
            journalsByAnimal[animalId].push(journal);
        });

        return (<>
            <div class={`w-100 px-3 py-2 mt-3 d-flex flex-column ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} rounded-lg`}>
                <div
                    class='d-flex flex-row justify-content-between align-items-center'
                    style='cursor: pointer;'
                    onClick={this.toggleJournalPicker}
                >
                    <div class='font-weight-bold'>
                        <i class={`fa-solid ${showJournalPicker ? 'fa-chevron-down' : 'fa-chevron-right'} me-2`} />
                        <Text id='dyrejournal.journal.add-more-journals'>Add more journals</Text>
                        {additionalJournalIds.length > 0 && (
                            <span class='badge bg-primary ms-2'>{additionalJournalIds.length}</span>
                        )}
                    </div>
                </div>

                {showJournalPicker && (
                    <div class='mt-2'>
                        {Object.keys(journalsByAnimal).map(animalId => {
                            const animal = animals.find(a => a.id === parseInt(animalId, 10));
                            const animalJournals = journalsByAnimal[animalId];

                            return (<>
                                {animals.length > 1 && animal && (
                                    <div class='fw-bold mt-2 mb-1'>{animal.name}</div>
                                )}
                                {animalJournals.map(journal => {
                                    const veterinary = veterinaries.find(vet => vet.id === journal.veterinary);
                                    const veterinaryName = veterinary ? `${veterinary.firstname} ${veterinary.lastname}` : journal.veterinaryName;
                                    const isSelected = additionalJournalIds.includes(journal.id);

                                    return (
                                        <div class={`d-flex flex-row align-items-start py-1 ${isSelected ? 'bg-primary-light' : ''}`}>
                                            <input
                                                type='checkbox'
                                                class='me-2 mt-1'
                                                value={journal.id}
                                                checked={isSelected}
                                                onInput={this.toggleAdditionalJournal}
                                            />
                                            <div class='d-flex flex-column'>
                                                <div>
                                                    <strong>#{journal.journalNumber || journal.id}</strong>
                                                    {' — '}
                                                    {util.formatDate(journal.date, { locale: 'nb-NO' })}
                                                    {veterinaryName && <span class='text-body-secondary ms-2'>({veterinaryName})</span>}
                                                </div>
                                                {journal.chiefComplaint && (
                                                    <small class='text-body-secondary'>{journal.chiefComplaint.substring(0, 100)}{journal.chiefComplaint.length > 100 ? '...' : ''}</small>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </>);
                        })}
                    </div>
                )}
            </div>
        </>);
    }

    renderAttachments() {
        const {
            selectedJournalFiles, selectedAnimalFiles, selectedIdexxResults, uploadedFiles = [],
            saveToAnimal = {}, additionalJournalIds = [],
        } = this.state;
        const { journals = [] } = this.props;
        const { journalStore, userStore } = this.props.stores;
        const { user } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');
        const { journalsToSend, allAnimalJournals, animals } = journalStore;
        const idexxResultsForSend = this.getIdexxResultsForSend();

        const originalJournalIds = journals.map(j => j.id);
        const effectiveJournals = [
            ...journalsToSend,
            ...(allAnimalJournals || []).filter(j => additionalJournalIds.includes(j.id)),
        ];

        // Collect journal files
        const journalFiles = [];
        effectiveJournals.forEach(journal => {
            if (journal.files && journal.files.length > 0) {
                journal.files.forEach(file => {
                    journalFiles.push({ ...file, journalId: journal.id });
                });
            }
        });

        // Collect animal files
        const animalFiles = [];
        animals.forEach(animal => {
            if (animal.files && animal.files.length > 0) {
                animal.files.forEach(file => {
                    animalFiles.push({ ...file, animalId: animal.id, animalName: animal.name, fileType: 'file' });
                });
            }
            if (animal.images && animal.images.length > 0) {
                animal.images.forEach(image => {
                    animalFiles.push({ ...image, animalId: animal.id, animalName: animal.name, fileType: 'image' });
                });
            }
        });

        const isJournalFileSelected = file => this.isJournalFileSelected(file, selectedJournalFiles);

        const hasAnyFiles = journalFiles.length > 0 || animalFiles.length > 0 || uploadedFiles.length > 0
            || idexxResultsForSend.length > 0;
        const hasSelectedJournalFiles = journalFiles.some(file => isJournalFileSelected(file));
        const hasSelectedIdexxResults = idexxResultsForSend.some(r => this.isIdexxResultSelected(r, selectedIdexxResults));
        const hasAnySelected = hasSelectedJournalFiles || selectedAnimalFiles.size > 0 || uploadedFiles.length > 0
            || hasSelectedIdexxResults;

        return (<>
            <div class='font-weight-lighter px-3 box-header mt-4 d-flex flex-row justify-content-between align-items-center'>
                <div>
                    <i class='fa-solid fa-paperclip me-2' />
                    <Text id='dyrejournal.journal.attachments'>Attachments</Text>
                </div>
                {hasAnySelected && (
                    <button
                        type='button'
                        class='btn btn-sm btn-outline-danger rounded-pill'
                        onClick={this.clearAllAttachments}
                    >
                        <i class='fa-solid fa-xmark me-1' />
                        <Text id='dyrejournal.journal.remove-all-attachments'>Remove all attachments</Text>
                    </button>
                )}
            </div>

            <div class={`w-100 px-3 py-2 mt-2 d-flex flex-column ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} rounded-lg`}>

                {/* Journal files */}
                {journalFiles.length > 0 && (<>
                    <div class='fw-bold mb-2'>
                        <Text id='dyrejournal.journal.journal-files'>Journal files</Text>
                    </div>
                    {journalFiles.map(file => (
                        <div class='d-flex flex-row align-items-center py-1'>
                            <input
                                type='checkbox'
                                class='me-2'
                                value={file.s3Link}
                                checked={isJournalFileSelected(file)}
                                onInput={this.toggleJournalFile}
                            />
                            <i class='fa-solid fa-file me-2 text-body-secondary' />
                            <span>{this.fileLabel(file)}</span>
                            <small class='text-body-secondary ms-2'>(Journal #{file.journalId})</small>
                            {file.bytes > 0 && <small class='text-body-secondary ms-2'>{this.formatBytes(file.bytes)}</small>}
                            {this.isImportedFile(file) && <small class='badge bg-warning text-dark ms-2'>
                                <i class='fa-solid fa-triangle-exclamation me-1' />
                                <Text id='dyrejournal.journal.imported-file-warning'>From the old system, check the content before sending</Text>
                            </small>}
                        </div>
                    ))}
                </>)}

                {/* Animal files */}
                {animalFiles.length > 0 && (<>
                    <div class='fw-bold mb-2 mt-3'>
                        <Text id='dyrejournal.journal.animal-files'>Animal files</Text>
                    </div>
                    {animalFiles.map(file => (
                        <div class='d-flex flex-row align-items-center py-1'>
                            <input
                                type='checkbox'
                                class='me-2'
                                value={file.s3Link}
                                checked={selectedAnimalFiles.has(file.s3Link)}
                                onInput={this.toggleAnimalFile}
                            />
                            <i class={`fa-solid ${file.fileType === 'image' ? 'fa-image' : 'fa-file'} me-2 text-body-secondary`} />
                            <span>{this.fileLabel(file)}</span>
                            <small class='text-body-secondary ms-2'>({file.animalName})</small>
                            {file.bytes > 0 && <small class='text-body-secondary ms-2'>{this.formatBytes(file.bytes)}</small>}
                        </div>
                    ))}
                </>)}

                {/* IDEXX lab results (TICKET-01579) */}
                {idexxResultsForSend.length > 0 && (<>
                    <div class='fw-bold mb-2 mt-3'>
                        <Text id='dyrejournal.journal.idexx-results'>IDEXX lab results</Text>
                    </div>
                    {idexxResultsForSend.map(result => (
                        <div class='d-flex flex-row align-items-center py-1'>
                            <input
                                type='checkbox'
                                class='me-2'
                                value={result.id}
                                checked={this.isIdexxResultSelected(result, selectedIdexxResults)}
                                onInput={this.toggleIdexxResult}
                            />
                            <i class='fa-solid fa-file-pdf me-2 text-body-secondary' />
                            <span>{result.testName || `IDEXX #${result.id}`}</span>
                            {result.resultDate && <small class='text-body-secondary ms-2'>{util.formatDate(result.resultDate, { locale: 'nb-NO' })}</small>}
                            <small class='text-body-secondary ms-2'>(Journal #{result.journal})</small>
                        </div>
                    ))}
                </>)}

                {/* Upload new files */}
                <div class='fw-bold mb-2 mt-3'>
                    <Text id='dyrejournal.journal.upload-files'>Upload files</Text>
                </div>

                <DragNDropFileupload
                    handleAddImage={this.handleUploadedFile}
                    apiUrl={'/api/fileupload'}
                    autoOpen={false}
                    accept='*/*'
                    text={<Text id='dyrejournal.journal.drop-files-here'>Drop files here or click to upload</Text>}
                    typeOfFile='file'
                />

                {uploadedFiles.length > 0 && (<>
                    <div class='mt-2'>
                        {uploadedFiles.map(file => (
                            <div class='d-flex flex-row align-items-center py-1'>
                                <i class='fa-solid fa-file me-2 text-body-secondary' />
                                <span>{file.name}</span>
                                {file.bytes > 0 && <small class='text-body-secondary ms-2'>{this.formatBytes(file.bytes)}</small>}
                                <div class='form-check ms-3'>
                                    <input
                                        type='checkbox'
                                        class='form-check-input'
                                        value={file.s3Link}
                                        checked={saveToAnimal[file.s3Link] || false}
                                        onInput={this.toggleSaveToAnimal}
                                    />
                                    <label class='form-check-label'>
                                        <small><Text id='dyrejournal.journal.save-to-animal'>Save to animal record</Text></small>
                                    </label>
                                </div>
                                <button
                                    type='button'
                                    class='btn btn-sm btn-outline-danger ms-2 rounded-pill'
                                    data-url={file.s3Link}
                                    onClick={this.removeUploadedFile}
                                >
                                    <i class='fa-solid fa-times' />
                                </button>
                            </div>
                        ))}
                    </div>
                </>)}

                {!hasAnyFiles && (
                    <div class='text-body-secondary py-2'>
                        <Text id='dyrejournal.journal.no-attachments'>No attachments yet. Upload files or select from journal/animal files above.</Text>
                    </div>
                )}
            </div>
        </>);
    }

    componentDidMount() {
        this.loadAll();
    }

    render() {
        const { journalSent, journalSending, toEmail, saleIds = [], includeImages, includeFiles, showJournalPicker, additionalJournalIds, selectedJournalFiles, selectedAnimalFiles, uploadedFiles, saveToAnimal, sendResponse, sendError } = this.state;
        const totalAttachmentBytes = this.getTotalAttachmentBytes();
        const SOFT_LIMIT = 5 * 1024 * 1024; // 5MB - warn the user (SES caps the whole email at ~10MB)
        const HARD_LIMIT = 7 * 1024 * 1024; // 7MB - backend will swap the largest files to links above this
        const sizeLevel = totalAttachmentBytes >= HARD_LIMIT ? 'danger' : (totalAttachmentBytes >= SOFT_LIMIT ? 'warning' : 'info');
        const {
            week,
            isNew,
            hideAdmin = true,
            drawerLevel = 1,
            showJournals = true,
            journals = [],
        } = this.props;
        const { appState, userStore, animalStore, journalStore, contactStore, saleStore } = this.props.stores;
        const { user, isAdmin } = userStore;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');
        const { animals, owners, journalsToSend, sales, veterinaries, deletedSales, visitorSales, allAnimalJournals } = journalStore;

        // Build the effective journal list (original + additional)
        const originalJournalIds = journals.map(j => j.id);
        const effectiveJournalsToSend = [
            ...journalsToSend,
            ...(allAnimalJournals || []).filter(j => additionalJournalIds.includes(j.id)),
        ];

        const { contacts } = contactStore;

        return (
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100 h-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 100px;'>

                    <div class='font-weight-lighter px-3 box-header'>
                        <Text id='dyrejournal.journal.send'>Send Journals</Text>
                    </div>

                    <div class={`w-100 px-2 py-2 px-3 mt-2 d-flex flex-row ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} rounded-lg`}>
                        <div class='w-20 font-weight-bold'>
                            <Text id='dyrejournal.animal-owner'>Owner</Text>
                        </div>
                        <div class='w-80 d-flex flex-column justify-content-start ms-3 flex-fill'>
                            {owners.map(owner => {
                                return (<>
                                    <div class='d-flex flex-column justify-content-start'>
                                        <div class=''>
                                            {owner.firstname} {owner.lastname}
                                        </div>
                                        <div class=''>
                                            {owner.address}
                                        </div>
                                        <div class=''>
                                            {fields.getNumberFixedWidth(owner.postalcode)} {owner.place}
                                        </div>
                                        <div class=''>
                                            {owner.email}
                                        </div>
                                        <div class=''>
                                            {owner.cellphone}
                                        </div>
                                    </div>
                                </>);
                            })}
                            {/* <xmp>{JSON.stringify(owners, null, 4)}</xmp> */}
                        </div>
                    </div>

                    {animals.some(a => a.insuranceCompany || a.insuranceId) && (
                        <div class={`w-100 px-3 py-2 mt-3 d-flex flex-row ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} rounded-lg`}>
                            <div class='w-20 font-weight-bold'>
                                <Text id='dyrejournal.insurance-title'>Forsikring</Text>
                            </div>
                            <div class='w-80 d-flex flex-column justify-content-start ms-3 flex-fill'>
                                {animals.map(animal => (
                                    (animal.insuranceCompany || animal.insuranceId) && (
                                        <div class='d-flex flex-column'>
                                            {animals.length > 1 && <div class='fw-bold'>{animal.name}:</div>}
                                            {animal.insuranceCompany && <div>{animal.insuranceCompany}</div>}
                                            {animal.insuranceId && <div>Forsikrings-ID: {animal.insuranceId}</div>}
                                        </div>
                                    )
                                ))}
                            </div>
                        </div>
                    )}

                    <div class={`w-100 px-3 py-2 mt-3 d-flex flex-row ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} rounded-lg`}>
                        <div class='w-20 font-weight-bold'>
                            <Text id='dyrejournal.animal-title'>Animal</Text>
                        </div>
                        <div class='w-80 d-flex flex-column justify-content-start ms-3 flex-fill'>
                            {animals.map(animal => <>
                                {animal.name}, {animal.species}, {animal.breed}, {animal.birth ? `f. ${util.formatDate(animal.birth, { locale: 'nb-NO' })} (${animalAge(animal.birth)})` : ''}
                            </>)}
                            {/* <xmp>{JSON.stringify(animal, null, 4)}</xmp> */}
                        </div>
                    </div>


                    <div class='font-weight-lighter px-3 box-header mt-4'>
                        <Text id='dyrejournal.journal.selected-journals'>Selected Journals</Text>
                    </div>

                    {effectiveJournalsToSend.length > 0 && effectiveJournalsToSend.map(journal => {
                        const veterinary = veterinaries.find(vet => vet.id === journal.veterinary);
                        const veterinaryName = veterinary ? `${veterinary.firstname} ${veterinary.lastname}` : journal.veterinaryName;

                        return (<>
                            <div class={`w-100 px-3 py-2 mt-3 d-flex flex-column ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} rounded-lg`}>
                                <div class='d-flex flex-row justify-content-start'>
                                    <div class='w-20 font-weight-bold'>
                                        <Text id='dyrejournal.journal-number'>Journal number</Text>
                                    </div>
                                    <div class='w-80 d-flex flex-column justify-content-start ms-3 flex-fill'>
                                        {journal.journalNumber || journal.id}
                                    </div>
                                </div>
                                <div class='d-flex flex-row justify-content-start mt-3'>
                                    <div class='w-20 font-weight-bold'>
                                        <Text id='dyrejournal.responsible-title'>Responsible</Text>
                                    </div>
                                    <div class='w-80 d-flex flex-column justify-content-start ms-3 flex-fill'>
                                        {veterinaryName}
                                    </div>
                                </div>
                                <div class='d-flex flex-row justify-content-start mt-3'>
                                    <div class='w-20 font-weight-bold'>
                                        <Text id='animal.journal-chiefComplaint'>chiefComplaint</Text>
                                    </div>
                                    <div class='w-80 d-flex flex-column justify-content-start ms-3 flex-fill'>
                                        <Markdown markdown={journal.chiefComplaint || ''} markedOpts={localUtil.getMarkdownOptions()} />
                                    </div>
                                </div>
                                {/* Empty fields are hidden here just like in journalPrint.js and the
                                    emailed PDF (lib/journal-pdf.js) — clinics that have switched a
                                    field off in journal settings should not see a blank row. */}
                                {journal.generalCondition ? <div class='d-flex flex-row justify-content-start mt-3'>
                                    <div class='w-20 font-weight-bold'>
                                        <Text id='animal.journal-generalCondition'>generalCondition</Text>
                                    </div>
                                    <div class='w-80 d-flex flex-column justify-content-start ms-3 flex-fill'>
                                        <Markdown markdown={journal.generalCondition || ''} markedOpts={localUtil.getMarkdownOptions()} />
                                    </div>
                                </div> : <></>}
                                {journal.physicalExaminationFindings ? <div class='d-flex flex-row justify-content-start mt-3'>
                                    <div class='w-20 font-weight-bold'>
                                        <Text id='animal.journal-physicalExaminationFindings'>physicalExaminationFindings</Text>
                                    </div>
                                    <div class='w-80 d-flex flex-column justify-content-start ms-3 flex-fill'>
                                        <Markdown markdown={journal.physicalExaminationFindings || ''} markedOpts={localUtil.getMarkdownOptions()} />
                                    </div>
                                </div> : <></>}
                                {journal.diagnosis && <>
                                    <div class='d-flex flex-row justify-content-start mt-3'>
                                        <div class='w-20 font-weight-bold'>
                                            <Text id='animal.journal-diagnosis'>diagnosis</Text>
                                        </div>
                                        <div class='w-80 d-flex flex-column justify-content-start ms-3 flex-fill'>
                                            <Markdown markdown={journal.diagnosis || ''} markedOpts={localUtil.getMarkdownOptions()} />
                                        </div>
                                    </div>
                                </>}
                                {journal.diagnosisCode && <>
                                    <div class='d-flex flex-row justify-content-start mt-3'>
                                        <div class='w-20 font-weight-bold'>
                                            <Text id='animal.journal-diagnosisCode'>diagnosisCode</Text>
                                        </div>
                                        <div class='w-80 d-flex flex-column justify-content-start ms-3 flex-fill'>
                                            {journal.diagnosisCode}
                                        </div>
                                    </div>
                                </>}

                                {journal.diagnoses && journal.diagnoses.length > 0 && <>
                                    <div class='d-flex flex-row justify-content-start mt-3'>
                                        <div class='w-20 font-weight-bold'>
                                            <Text id='animal.journal-diagnoses'>Diagnoses</Text>
                                        </div>
                                        <div class='w-80 d-flex flex-column justify-content-start ms-3 flex-fill'>
                                            <table class='table table-sm table-striped'>
                                                <thead>
                                                    <tr>
                                                        <th><Text id='animal.journal-diagnoses-diagnoseCode'>Diagnose code</Text></th>
                                                        <th><Text id='animal.journal-diagnoses-name'>Name</Text></th>
                                                        <th><Text id='animal.journal-diagnoses-date'>Date</Text></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                {journal.diagnoses.map(diagnose => {
                                                    return (<>

                                                        <tr>
                                                            <td>{diagnose.diagnoseCode}</td>
                                                            <td>{diagnose.name}{!!diagnose.differential && <> <Text id='dyrejournal.diagnose-differential-suffix'>(differential diagnosis)</Text></>}</td>
                                                            <td>{util.formatDate(diagnose.date, { hour12: false, hour: '2-digit', minute: '2-digit', locale: 'nb-NO' })}</td>
                                                        </tr>
                                                    </>);
                                                })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </>}
                                {journal.medicine && journal.medicine.length > 0 && <>
                                    <div class='d-flex flex-row justify-content-start mt-3'>
                                        <div class='w-20 font-weight-bold'>
                                            <Text id='animal.journal-medicine'>Medicine</Text>
                                        </div>
                                        <div class='w-80 d-flex flex-column justify-content-start ms-3 flex-fill'>
                                            <table class='table table-sm table-striped'>
                                                <thead>
                                                    <tr>
                                                        <th><Text id='animal.journal-medicine-name'>Name</Text></th>
                                                        <th><Text id='animal.journal-medicine-amountPerAnimal'>Amount per animal</Text></th>
                                                        <th><Text id='animal.journal-medicine-unit'>Unit</Text></th>
                                                        <th><Text id='animal.journal-medicine-routeOfAdministration'>Route</Text></th>
                                                        <th><Text id='animal.journal-medicine-lastThreatment'>Last treatment</Text></th>
                                                        <th><Text id='animal.journal-medicine-usageReason'>Usage reason</Text></th>
                                                        <th><Text id='animal.journal-medicine-threatmentBy'>Treatment by</Text></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                {journal.medicine.map(med => {
                                                    return (<>
                                                        <tr>
                                                            <td>{med.name}</td>
                                                            <td>{med.amountPerAnimal}</td>
                                                            <td>{med.unit}</td>
                                                            <td>{med.routeOfAdministration}</td>
                                                            <td>{util.formatDate(med.lastThreatment, { hour12: false, hour: '2-digit', minute: '2-digit', locale: 'nb-NO' })}</td>
                                                            <td>{localUtil.getUsageReason(med.usageReason)}</td>
                                                            <td>{localUtil.getThreatmentBy(med.threatmentBy)}</td>
                                                        </tr>
                                                    </>);
                                                })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </>}

                                {journal.prescriptions && journal.prescriptions.length > 0 && <>
                                    <div class='d-flex flex-row justify-content-start mt-3'>
                                        <div class='w-20 font-weight-bold'>
                                            <Text id='dyrejournal.journal.prescriptions'>Prescriptions</Text>
                                        </div>
                                        <div class='w-80 d-flex flex-column justify-content-start ms-3 flex-fill'>
                                            <DyrejournalPrescriptionListMini
                                                classNames=''
                                                stores={this.props.stores}
                                                prescriptions={journal.prescriptions}
                                                saleIsLocked={true}
                                                showButtons={false}
                                            />
                                        </div>
                                    </div>
                                </>}

                                {journal.diagnosticTests && <>
                                    <div class='d-flex flex-row justify-content-start mt-3'>
                                        <div class='w-20 font-weight-bold'>
                                            <Text id='animal.journal-diagnosticTests'>diagnosticTests</Text>
                                        </div>
                                        <div class='w-80 d-flex flex-column justify-content-start ms-3 flex-fill'>
                                            <Markdown markdown={journal.diagnosticTests || ''} markedOpts={localUtil.getMarkdownOptions()} />
                                        </div>
                                    </div>
                                </>}
                                {journal.treatmentPlan && <>
                                    <div class='d-flex flex-row justify-content-start mt-3'>
                                        <div class='w-20 font-weight-bold'>
                                            <Text id='animal.journal-treatmentPlan'>treatmentPlan</Text>
                                        </div>
                                        <div class='w-80 d-flex flex-column justify-content-start ms-3 flex-fill'>
                                            <Markdown markdown={journal.treatmentPlan || ''} markedOpts={localUtil.getMarkdownOptions()} />
                                        </div>
                                    </div>
                                </>}
                                {journal.clientCommunication && <>
                                    <div class='d-flex flex-row justify-content-start mt-3'>
                                        <div class='w-20 font-weight-bold'>
                                            <Text id='animal.journal-clientCommunication'>clientCommunication</Text>
                                        </div>
                                        <div class='w-80 d-flex flex-column justify-content-start ms-3 flex-fill'>
                                            <Markdown markdown={journal.clientCommunication || ''} markedOpts={localUtil.getMarkdownOptions()} />
                                        </div>
                                    </div>
                                </>}
                                {journal.recommendations && <>
                                    <div class='d-flex flex-row justify-content-start mt-3'>
                                        <div class='w-20 font-weight-bold'>
                                            <Text id='animal.journal-recommendations'>recommendations</Text>
                                        </div>
                                        <div class='w-80 d-flex flex-column justify-content-start ms-3 flex-fill'>
                                            <Markdown markdown={journal.recommendations || ''} markedOpts={localUtil.getMarkdownOptions()} />
                                        </div>
                                    </div>
                                </>}
                                {journal.followUp && <>
                                    <div class='d-flex flex-row justify-content-start mt-3'>
                                        <div class='w-20 font-weight-bold'>
                                            <Text id='animal.journal-followUp'>followUp</Text>
                                        </div>
                                        <div class='w-80 d-flex flex-column justify-content-start ms-3 flex-fill'>
                                            <Markdown markdown={journal.followUp || ''} markedOpts={localUtil.getMarkdownOptions()} />
                                        </div>
                                    </div>
                                </>}
                                {journal.comment && <>
                                    <div class='d-flex flex-row justify-content-start mt-3'>
                                        <div class='w-20 font-weight-bold'>
                                            <Text id='animal.journal-comment'>comment</Text>
                                        </div>
                                        <div class='w-80 d-flex flex-column justify-content-start ms-3 flex-fill'>
                                            <Markdown markdown={journal.comment || ''} markedOpts={localUtil.getMarkdownOptions()} />
                                        </div>
                                    </div>
                                </>}
                                {journal.foodType && <>
                                    <div class='d-flex flex-row justify-content-start mt-3'>
                                        <div class='w-20 font-weight-bold'>
                                            <Text id='animal.journal-foodType'>foodType</Text>
                                        </div>
                                        <div class='w-80 d-flex flex-column justify-content-start ms-3 flex-fill'>
                                            {journal.foodType}
                                        </div>
                                    </div>
                                </>}

                                {includeImages && journal.images && journal.images.length > 0 ? <>
                                    <div class='d-flex flex-row justify-content-start mt-3'>
                                        <div class='w-20 font-weight-bold'>
                                            <Text id='animal.journal-images'>images</Text>
                                        </div>
                                        <div class='w-80 d-flex flex-column justify-content-start ms-3 flex-fill'>
                                            {journal.images.map(image => <>
                                                <a href={image.s3Link} target='_blank'>
                                                    <img src={image.s3MediumLink} style="max-width: 50%;" />
                                                </a>
                                            </>)}
                                        </div>
                                    </div>
                                </> : ''}
                                {includeFiles && journal.files && journal.files.length > 0 ? <>
                                    <div class='d-flex flex-row justify-content-start mt-3'>
                                        <div class='w-20 font-weight-bold'>
                                            <Text id='animal.journal-files'>files</Text>
                                        </div>
                                        <div class='w-80 d-flex flex-column justify-content-start ms-3 flex-fill'>
                                            {journal.files.map(file => <>
                                                <a href={file.s3Link} target='_blank'>
                                                    {file.name}
                                                </a>
                                            </>)}
                                        </div>
                                    </div>
                                </> : ''}

                                {/* <xmp>{JSON.stringify(journal, null, 4)}</xmp> */}
                            </div>
                        </>);
                    })}

                    {this.renderJournalPicker()}

                    <div class='font-weight-lighter px-3 box-header mt-4'>
                        <Text id='dyrejournal.journal.email-composition'>Email</Text>
                    </div>

                    <div class={`w-100 px-3 py-2 mt-2 d-flex flex-column`}>
                        <div class='form-inline'>
                            <label class='sr-only'>Email</label>
                            <div class='input-group mb-2 mr-sm-2'>
                                <div class='input-group-text rounded-pill-left'>@</div>
                                <input
                                    type='text'
                                    class='form-control rounded-pill-right'
                                    placeholder='Email'
                                    value={toEmail}
                                    onInput={this.searchInput}
                                    onKeyDown={this.handleKeyDown}
                                />
                            </div>
                        </div>

                        {contacts && contacts.length > 0 && <>
                            <div class='d-flex w-100'>
                                <div class='table-responsive'>
                                    <table class={`table table-sm table-hover ${darkmode ? 'table-dark' : 'table-light'}`}>
                                        <thead>
                                            <tr>
                                                <th>&nbsp;</th>
                                                <th><Text id='input.name'>Name</Text></th>
                                                <th><Text id='input.email'>Email</Text></th>
                                                <th><Text id='input.cellphone'>Cellphone</Text></th>
                                                <th><Text id='input.description'>Description</Text></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {contacts.map(contact => {
                                                return <tr
                                                    data-email={contact.email}
                                                    data-id={contact.id}
                                                    onClick={this.onClickContact}
                                                >
                                                    <td class='d-none d-sm-table-cell' onClick={this.onClickEditContact}>
                                                        <i class='fa-solid fa-pen' />
                                                    </td>
                                                    <td>{contact.name ? `${contact.name}` : `${contact.firstname} ${contact.lastname}`}</td>
                                                    <td>{contact.email}</td>
                                                    <td>{contact.cellphone}</td>
                                                    <td>{contact.description}</td>
                                                </tr>;
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </>}

                        <div class='form-inline'>
                            <label class='sr-only'>Subject</label>
                            <div class='input-group mb-2 mr-sm-2'>
                                <div class='input-group-text rounded-pill-left'>S</div>
                                <input type='text' class='form-control rounded-pill-right' placeholder='Subject' onInput={linkstate(this, 'subject')} />
                            </div>
                        </div>
                        <div class='form'>
                            <label class='sr-only'>Body</label>
                            <div class='input-group mb-2 mr-sm-2'>
                                <div class='input-group-text rounded-pill-left'>B</div>
                                <textarea type='text' class='form-control rounded-pill-right' placeholder='Body' onInput={linkstate(this, 'body')} />
                            </div>
                        </div>
                        <div class='form-check mb-2 mr-sm-2'>
                            <input
                                id='includeBodyText'
                                class='form-check-input'
                                type='checkbox'
                                checked={this.state.includeBodyText}
                                onInput={linkstate(this, 'includeBodyText')}
                            />
                            <label class='form-check label' for='includeBodyText'>
                                <Text id='dyrejournal.journal-send-include-body-text'>Include message in PDF</Text>
                            </label>
                        </div>
                        <div class='form-check mb-2 mr-sm-2'>
                            <input
                                id='includeImages'
                                class='form-check-input'
                                type='checkbox'
                                value='1'
                                checked={this.state.includeImages}
                                onInput={linkstate(this, 'includeImages')}
                            />
                            <label class='form-check label' for='includeImages'>
                                <Text id='dyrejournal.journal-send-include-images'>Include Images</Text>
                            </label>
                        </div>
                        {(sales && sales.length > 0) ? <>
                            <div class='form-check mb-2 mr-sm-2'>
                                <input
                                    id='includeReceipt'
                                    class='form-check-input'
                                    type='checkbox'
                                    onInput={linkstate(this, 'includeReceipt')}
                                    disabled={(sales && sales.length > 0) ? false : true}
                                />
                                <label class='form-check label' for='includeReceipt'>
                                    <Text id='dyrejournal.journal-send-include-receipts'>Include receipt/invoice</Text>
                                </label>
                            </div>
                        </> : ''}
                    </div>


                    <div class='font-weight-lighter px-3 box-header mt-4'>
                        <Text id='dyrejournal.journal.related-sales'>Related sales</Text>
                    </div>

                    <div class={`w-100 px-2 py-2 px-3 mt-2 d-flex flex-row ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} rounded-lg`}>
                        <div class={`w-100 px-3 py-2 mt-2 d-flex flex-column`}>
                            {(sales && sales.length > 0) ? <>
                                {sales.map(sale => <>
                                    <div>
                                        {util.formatDate(sale.saleDate, { hour12: false, hour: '2-digit', minute: '2-digit', locale: 'nb-NO' })}: {sale.title}
                                    </div>
                                </>)}
                            </> : <>
                                <div class='d-flex flex-row justify-content-start'>
                                    <i class='fa-solid fa-times text-danger me-2' style='font-size: 1.6em;' />
                                    <Text id='dyrejournal.no-related-sales'>No related sales to the selected journals.</Text>
                                </div>

                                {(deletedSales && deletedSales.length > 0) && <>
                                    <div class='font-weight-bold mt-2'>
                                        <Text id='dyrejournal.journal.deleted-sales'>Deleted sales related to this journal</Text>:
                                    </div>
                                    {deletedSales.map(sale => <>
                                        <div class='text-muted text-strikethrough'>
                                            {util.formatDate(sale.saleDate, { hour12: false, hour: '2-digit', minute: '2-digit', locale: 'nb-NO' })}: {sale.title} <nobr>kr.{saleStore.sumTotal(sale.products, true)}</nobr>
                                        </div>
                                    </>)}
                                </>}
                            </>}

                            {/* saleIds: {saleIds.join(', ')} */}
                            {(visitorSales && visitorSales.length > 0) && <>
                                <div class='font-weight-bold mt-2'>
                                    <Text id='dyrejournal.journal.visitor-sales'>Other sales for this visitor</Text>:
                                </div>
                                {visitorSales.map(sale => <>
                                    <div class={`text-normal ${saleIds.includes(`${sale.id}`) ? 'bg-primary-light' : ''}`}>
                                        <input
                                            type='checkbox'
                                            class='me-2'
                                            value={sale.id}
                                            onInput={this.addSaleId}
                                        />
                                        {util.formatDate(sale.saleDate, { hour12: false, hour: '2-digit', minute: '2-digit', locale: 'nb-NO' })}: {sale.title} <nobr>kr.{saleStore.sumTotal(sale.products, true)}</nobr>

                                    </div>
                                </>)}
                            </>}

                        </div>
                    </div>

                    {this.renderAttachments()}

                    {totalAttachmentBytes > 0 && (
                        <div class={`w-100 px-3 mt-3 alert alert-${sizeLevel} rounded-lg mb-0`}>
                            <i class='fa-solid fa-paperclip me-2' />
                            <Text id='dyrejournal.attachment-total-size' fields={{ size: this.formatBytes(totalAttachmentBytes) }}>
                                Total attachment size: {this.formatBytes(totalAttachmentBytes)}
                            </Text>
                            {sizeLevel === 'warning' && <>
                                <br /><small><Text id='dyrejournal.attachment-size-warning'>Approaching the email size limit. Consider deselecting some files.</Text></small>
                            </>}
                            {sizeLevel === 'danger' && <>
                                <br /><small><Text id='dyrejournal.attachment-size-danger'>The largest files will be sent as download links instead of attachments.</Text></small>
                            </>}
                        </div>
                    )}

                    {journalSent && sendResponse?.linkedAttachments?.length > 0 && (
                        <div class='w-100 px-3 mt-3 alert alert-warning rounded-lg mb-0'>
                            <i class='fa-solid fa-link me-2' />
                            <Text id='dyrejournal.attachment-linked-info' fields={{ count: sendResponse.linkedAttachments.length }}>
                                {sendResponse.linkedAttachments.length} large attachment(s) were too big and were sent as download links in the email body.
                            </Text>
                            <ul class='mb-0 mt-2'>
                                {sendResponse.linkedAttachments.map(a => (
                                    <li><small>{a.filename} — {this.formatBytes(a.bytes)}</small></li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {journalSent && sendResponse?.idexxPdfFailed?.length > 0 && (
                        <div class='w-100 px-3 mt-3 alert alert-warning rounded-lg mb-0'>
                            <i class='fa-solid fa-triangle-exclamation me-2' />
                            <Text id='dyrejournal.idexx-pdf-failed-info' fields={{ count: sendResponse.idexxPdfFailed.length }}>
                                {sendResponse.idexxPdfFailed.length} IDEXX lab result(s) could not be fetched and were not attached. Download the PDF from the journal and send it separately.
                            </Text>
                        </div>
                    )}

                    {sendError && sendError.length > 0 && (
                        <div class='w-100 px-3 mt-3 alert alert-danger rounded-lg mb-0'>
                            <i class='fa-solid fa-triangle-exclamation me-2' />
                            <Text id='dyrejournal.email-journal-failed'>The journal could not be sent by email.</Text>
                            <ul class='mb-0 mt-2'>
                                {sendError.map(f => (
                                    <li><small>{f.to ? `${f.to}: ` : ''}{this.sendErrorText(f.error)}</small></li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <div class={`w-100 px-3 py-2 mt-2 d-flex flex-row`}>
                        <button
                            class={`btn btn-info ms-2 rounded-pill`}
                            onClick={this.onClickPrintJournal}
                        >
                            <i class='fa-solid fa-print' /> <Text id='dyrejournal.print-journal'>Print journal</Text>
                        </button>
                        <button
                            type='button'
                            class={`btn ${journalSent ? 'btn-success' : 'btn-primary'} ms-2 rounded-pill`}
                            onClick={this.onClickEmailJournal}
                            disabled={journalSending}
                        >
                            {journalSending ? <>
                                <i class='fa-solid fa-spinner-third fa-spin' /> <Text id='dyrejournal.email-journal-sending'>Sending email...</Text>
                            </> : <>
                                {journalSent ? <>
                                    <i class='fa-solid fa-check' /> <Text id='dyrejournal.journal-sent'>Journal sent</Text>
                                </> : <>
                                    <i class='fa-solid fa-paper-plane' /> <Text id='dyrejournal.email-journal-to-address'>Email journal to address</Text>
                                </>}
                            </>}
                        </button>
                    </div>

                    {/* {journalSent && <div class='w-100 d-flex px-3 mt-4 justify-content-center'>
                        <div class='alert alert-success rounded-pill' role='alert'>
                            <Text id='dyrejournal.journal-sent'>Journal sent</Text>
                        </div>
                    </div>} */}

                </div>
            </div>
        );
    }
}

export default SendJournal;
